/**
 * Every error this SDK throws is a `GGCoinError`. The `code` field
 * mirrors the api's stable machine code (`INVALID_CREDENTIALS`,
 * `WALLET_NOT_FOUND`, etc.) so callers can `switch` exhaustively without
 * parsing messages or HTTP status codes.
 *
 * Stable codes the api currently emits (see `apps/api/src/middleware/errors.ts`):
 *
 *   VALIDATION_ERROR           400 — Zod parse failure (request body)
 *   ACCOUNT_EXISTS             409 — duplicate email on register
 *   INVALID_CREDENTIALS        401 — wrong email / password / TOTP (uniform)
 *   TOTP_NOT_ENROLLED          403 — login refused; finish 2FA setup
 *   TOTP_ALREADY_ENROLLED      409 — duplicate confirm-totp call
 *   WEAK_PASSWORD              400 — password policy failure
 *   UNAUTHENTICATED            401 — JWT missing / expired / revoked
 *   AUTH_ERROR                 400 — generic auth error
 *   WALLET_NOT_FOUND           404 — wallet / sub-wallet not found
 *   WALLET_FROZEN              403 — wallet is frozen
 *   INVALID_SUB_WALLET_CONFIG  400 — bad spendCap / approvalThreshold
 *   LEDGER_POSTING_MISMATCH    400 — posting body's tx id doesn't match
 *   TRANSACTION_NOT_FOUND      404 — reverse target missing
 *   TRANSACTION_NOT_PENDING    409 — post target isn't pending
 *   TRANSACTION_NOT_POSTED     409 — reverse target isn't posted
 *   INTEGRITY_ERROR            500 — db / ledger integrity bug
 *   LEDGER_ERROR               400 — generic ledger error
 *   MALFORMED_JSON             400 — request body is not valid JSON
 *   INTERNAL_ERROR             500 — fallback
 */
export class GGCoinError extends Error {
  /** Stable machine code from the api response (or a SDK-side code). */
  readonly code: string;
  /** HTTP status, or `0` when the failure happened before a response (network, abort). */
  readonly status: number;

  /**
   * @param code — stable machine code (`INVALID_CREDENTIALS`, etc.).
   * @param status — HTTP status, or `0` for pre-response failures (network).
   * @param message — single-line summary; goes verbatim to the user.
   */
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = "GGCoinError";
    this.code = code;
    this.status = status;
  }
}
